import { Separator } from '@/components/ui/separator'
import { selectedRiskProfileWorkspaceAtom } from '@app/state/riskAtoms'
import { RiskWorkspace } from '@app/types/workspaceTypes'
import { useAtomValue } from 'jotai'
import { useEffect, useMemo, useRef } from 'react'
import { useRiskFilters } from '../../shared/hooks/useRiskFilters'
import RiskGroupMultiGroupSelect from '../components/RiskProfileMultiGroupSelector'
import { useElementGroupBuckets } from '../hooks/useElementGroupBuckets'
import { useHazardGroupBuckets } from '../hooks/useHazardGroupBuckets'
import { useLayerLookup } from '../hooks/useLayerLookup'
import { useRegionsSelector } from './RegionsSelector'
import { RiskProfileDamageStateFilter } from './RiskProfileDamageStateFilter'
import { RiskProfileElementsSelector } from './RiskProfileElementsSelector'
import RiskProfileHazardSelector from './RiskProfileHazardSelector'
import WorkspaceSelector from './WorkspaceSelector'

const RiskProfileFiltersGrid = () => {
  const { getTitle } = useLayerLookup()
  const selectedWorkspace = useAtomValue(selectedRiskProfileWorkspaceAtom)
  const { data: regions = [], isLoading: isLoadingRegions } = useRegionsSelector()

  const {
    areaGroup,
    areas,
    hazardGroups,
    elementGroups,
    damageStates,
    setFilters,
    setAnalysisMode,
  } = useRiskFilters()

  const hasSelectedWorkspace = selectedWorkspace !== null
  const hasRegions = !isLoadingRegions && regions.length > 0

  const areaGroups = useMemo(
    () =>
      regions.reduce<Record<string, string[]>>((groups, region) => {
        groups[region.group_name] ??= []
        groups[region.group_name].push(region.region_name)
        return groups
      }, {}),
    [regions],
  )

  const hazardGroupBuckets = useHazardGroupBuckets(selectedWorkspace?.layers.scenarios ?? [])

  const hazardGroupNames = useMemo(
    () => hazardGroupBuckets.map((bucket) => Object.keys(bucket)[0]),
    [hazardGroupBuckets],
  )

  const elementGroupBuckets = useElementGroupBuckets(selectedWorkspace?.layers.assets ?? [])

  const defaultedWorkspaceIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!selectedWorkspace) return
    if (defaultedWorkspaceIdRef.current === selectedWorkspace.id) return

    defaultedWorkspaceIdRef.current = selectedWorkspace.id

    if (elementGroups.length === 0 && hazardGroups.length === 0) {
      const allElementIds = elementGroupBuckets.flatMap((bucket) => Object.values(bucket)[0])
      setFilters({ elementGroups: allElementIds, hazardGroups: hazardGroupNames })
    } else if (elementGroups.length === 0) {
      const allElementIds = elementGroupBuckets.flatMap((bucket) => Object.values(bucket)[0])
      setFilters({ elementGroups: allElementIds })
    } else if (hazardGroups.length === 0) {
      setFilters({ hazardGroups: hazardGroupNames })
    }
  }, [
    selectedWorkspace,
    elementGroupBuckets,
    hazardGroupNames,
    elementGroups,
    hazardGroups,
    setFilters,
  ])

  const handleWorkspaceSelect = (workspace: RiskWorkspace) =>
    setFilters({
      workspaceId: workspace.id,
      areaGroup: null,
      areas: [],
      hazardGroups: [],
      elementGroups: [],
      damageStates: [],
    })

  const handleElementsChange = (ids: string[]) => {
    setFilters({ elementGroups: ids })

    if (ids.length === 1) {
      setAnalysisMode('by-type')
    }
  }

  return (
    <section className="border-b bg-neutral-100 px-9 py-6">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-5">
        <div className="flex flex-wrap items-end justify-center gap-7">
          <WorkspaceSelector onSelect={handleWorkspaceSelect} />

          <Separator orientation="vertical" className="hidden h-10 bg-foreground xl:block" />

          {hasRegions && (
            <RiskGroupMultiGroupSelect
              label="Area"
              placeholder="Whole region"
              groups={areaGroups}
              activeGroup={areaGroup}
              onGroupChange={(group) => setFilters({ areaGroup: group, areas: [] })}
              selected={areas}
              onChange={(nextAreas) => setFilters({ areas: nextAreas })}
              disabled={!hasSelectedWorkspace}
            />
          )}

          <RiskProfileElementsSelector
            label="Elements"
            placeholder="All elements"
            groups={elementGroupBuckets}
            selected={elementGroups}
            onChange={handleElementsChange}
            getLabel={getTitle}
            disabled={!hasSelectedWorkspace}
          />

          <RiskProfileHazardSelector
            label="Hazards"
            placeholder="All hazards"
            options={hazardGroupNames}
            selected={hazardGroups}
            onChange={(nextHazards) => setFilters({ hazardGroups: nextHazards })}
            disabled={!hasSelectedWorkspace}
          />
        </div>

        <div className="flex justify-end gap-2.5">
          <RiskProfileDamageStateFilter
            damageStates={damageStates}
            onApply={(nextDamageStates) => setFilters({ damageStates: nextDamageStates })}
          />
        </div>
      </div>
    </section>
  )
}

export default RiskProfileFiltersGrid
